import {
  FormEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AirOutlined,
  ChevronLeft,
  ChevronRight,
  Inventory2Outlined,
  PictureAsPdfOutlined,
  RestartAlt,
} from '@mui/icons-material';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { WideLayout } from '../../../../../shared/layout/widelayout';
import { useResponsive } from '../../../../../hooks/useResponsive';
import { Spinner } from '../../../../../shared/ui/Spinners';
import { downloadPDF, getPdfDownloadUrl } from '../../../../../utils/documents';
import { getLastFromSplit } from '../../../../../utils/text';
import { useOrderFlowStore } from '../../../store/newOrderFlowStore';
import { useApiQuestionnaire } from './basketApi';
import {
  firstMissing,
  groupBlocks,
  isAnswered,
  modelChoiceOf,
  SelectedItem,
  tabOf,
  toOrderData,
} from './flow';
import { ModelPicker } from './ModelPicker';
import { useProduct, useProductGuides, useProductImage } from './productApi';
import { QuestionBlocks } from './QuestionBlocks';
import { BasketResponseDTO, DbProduct, FlowTab } from './types';
import { useQuestionnaire } from './useQuestionnaire';
import {
  BlockNumber,
  Card,
  CardTabsBar,
  ContentGrid,
  EmptyState,
  ExtraCard,
  ExtrasBlock,
  ExtrasEmpty,
  ExtrasHeader,
  ExtrasRail,
  ExtrasTitle,
  FileGrid,
  FileTile,
  FitBody,
  FitHead,
  ImageBox,
  ModelDialog,
  NavButton,
  NavFooter,
  PageShell,
  PageTitle,
  ProductIdInput,
  ProductSearchForm,
  ProductBody,
  ProductCard,
  ProductColumn,
  ProductHead,
  ProductInfo,
  ProductName,
  ProductText,
  ProgressFill,
  ProgressTrack,
  QuestionColumn,
  RailButtons,
  SectionDivider,
  SectionTab,
  SectionTabs,
  StepBody,
  StepHeader,
  StepLabel,
  TabBar,
  TabButton,
  TextAction,
  TitleRow,
} from './styles';

const TABS: Array<{ id: FlowTab; label: string }> = [
  { id: 'principal', label: 'Terapia Principal - Acessórios' },
  { id: 'complementar', label: 'Terapias complementares' },
];

/** Paciente do pedido em andamento · equipamento · categoria. */
const ContextLine = ({
  productId,
  product,
}: {
  productId: number;
  product: DbProduct | null;
}) => {
  const patient = useOrderFlowStore((state) => state.selectedPatient);
  return (
    <small>
      {[
        patient?.name,
        product?.description ?? `Produto #${productId}`,
        product?.product_category?.description,
      ]
        .filter(Boolean)
        .join(' · ')}
    </small>
  );
};

// ─── Coluna da direita: equipamento e produtos adicionais ─────────────────────

/** Foto do produto adicionado; ícone neutro sem foto. */
const ExtraImage = ({ sapCode }: { sapCode?: string }) => {
  const src = useProductImage(sapCode);
  return <div>{src ? <img src={src} alt="" /> : <Inventory2Outlined />}</div>;
};

export const DescriptionPanel = ({
  productId,
  product,
  extras,
  blockNumber,
  embedded = false,
  extrasStatus,
}: {
  productId: number;
  product: DbProduct | null;
  extras: SelectedItem[];
  /** Número do bloco da pergunta que adicionou o produto. */
  blockNumber: (questionCode: string) => number | undefined;
  embedded?: boolean;
  extrasStatus?: ReactNode;
}) => {
  const [tab, setTab] = useState<'description' | 'files'>('description');
  const image = useProductImage(product?.sap_code);
  const files = useProductGuides(
    tab === 'files' ? product?.sap_code : undefined
  );
  const rail = useRef<HTMLDivElement>(null);
  const scrollRail = (direction: -1 | 1) =>
    rail.current?.scrollBy({ left: direction * 480, behavior: 'smooth' });

  const explanation =
    product?.explanation?.trim() || product?.explanation1?.trim() || '';
  const technical = product?.technical_information?.trim() ?? '';

  const tabBar = (
    <TabBar role="tablist">
      <TabButton
        type="button"
        role="tab"
        active={tab === 'description'}
        aria-selected={tab === 'description'}
        onClick={() => setTab('description')}
      >
        Descrição
      </TabButton>
      <TabButton
        type="button"
        role="tab"
        active={tab === 'files'}
        aria-selected={tab === 'files'}
        onClick={() => setTab('files')}
      >
        Arquivos
      </TabButton>
    </TabBar>
  );

  const filesContent = files.loading ? (
    <EmptyState>Carregando arquivos…</EmptyState>
  ) : files.guides.length === 0 ? (
    <EmptyState>Nenhum arquivo disponível.</EmptyState>
  ) : (
    <FileGrid>
      {files.guides.map(({ productCode, guide }) => (
        <FileTile
          key={`${productCode}-${guide}`}
          type="button"
          title={getLastFromSplit(guide, '_')}
          onClick={() => downloadPDF(getPdfDownloadUrl(productCode, guide))}
        >
          <PictureAsPdfOutlined />
          {getLastFromSplit(guide, '_')}
        </FileTile>
      ))}
    </FileGrid>
  );

  const head = (
    <ProductHead
      sx={
        embedded
          ? { flexDirection: { xs: 'column', md: 'row' }, gap: 2 }
          : undefined
      }
    >
      <ImageBox
        aria-hidden="true"
        sx={
          embedded ? { width: { xs: '100%', md: 140 }, height: 160 } : undefined
        }
      >
        {image ? <img src={image} alt="" /> : <AirOutlined />}
      </ImageBox>
      <ProductInfo>
        <ProductName>
          {product?.description ?? `Produto #${productId}`}
        </ProductName>
        <ProductText sx={{ whiteSpace: 'pre-line' }}>
          {explanation || 'Nenhuma descrição disponível.'}
        </ProductText>
        {technical && (
          <>
            <SectionDivider />
            <ProductText sx={{ whiteSpace: 'pre-line' }}>
              {technical}
            </ProductText>
          </>
        )}
      </ProductInfo>
    </ProductHead>
  );

  const extrasContent = (
    <>
      <ExtrasHeader>
        <ExtrasTitle>Produtos adicionais</ExtrasTitle>
        {extras.length > 0 && (
          <RailButtons>
            <IconButton aria-label="Anterior" onClick={() => scrollRail(-1)}>
              <ChevronLeft />
            </IconButton>
            <IconButton aria-label="Próximo" onClick={() => scrollRail(1)}>
              <ChevronRight />
            </IconButton>
          </RailButtons>
        )}
      </ExtrasHeader>
      {extrasStatus ? (
        extrasStatus
      ) : extras.length === 0 ? (
        <ExtrasEmpty>
          Os produtos escolhidos nas perguntas aparecem aqui.
        </ExtrasEmpty>
      ) : (
        <ExtrasRail ref={rail}>
          {extras.map((item) => (
            <ExtraCard key={item.key}>
              <BlockNumber
                sx={{ position: 'absolute', top: '6px', left: '6px' }}
                title={`Bloco ${blockNumber(item.questionCode)}`}
              >
                {blockNumber(item.questionCode)}
              </BlockNumber>
              <ExtraImage sapCode={item.sapCode} />
              <strong>
                {item.name}
                {item.quantity && item.quantity > 1
                  ? ` (${item.quantity} un.)`
                  : ''}
              </strong>
            </ExtraCard>
          ))}
        </ExtrasRail>
      )}
    </>
  );

  return (
    <ProductCard
      sx={embedded ? { boxShadow: 'none', alignSelf: 'start' } : undefined}
    >
      {tabBar}
      {embedded ? (
        <>
          <ProductBody sx={{ maxHeight: 420, overflowY: 'auto' }}>
            {tab === 'description' ? head : filesContent}
          </ProductBody>
          <ExtrasBlock
            sx={{ p: 2, borderTop: '1px solid #e7ece8', minWidth: 0 }}
          >
            {extrasContent}
          </ExtrasBlock>
        </>
      ) : tab === 'description' ? (
        <FitBody>
          <FitHead>{head}</FitHead>
          <ExtrasBlock>
            <SectionDivider sx={{ margin: '12px 0' }} />
            {extrasContent}
          </ExtrasBlock>
        </FitBody>
      ) : (
        <ProductBody>{filesContent}</ProductBody>
      )}
    </ProductCard>
  );
};

// ─── Diálogos ─────────────────────────────────────────────────────────────────

/** Confirmação para ações que apagam o que foi feito (reiniciar, restaurar). */
export const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}) => (
  <ModelDialog open={open} onClose={onClose} aria-labelledby="confirm-title">
    <DialogTitle id="confirm-title">{title}</DialogTitle>
    <DialogContent>
      <ProductText sx={{ margin: 0 }}>{message}</ProductText>
    </DialogContent>
    <DialogActions sx={{ padding: '0 24px 20px', gap: '8px' }}>
      <NavButton onClick={onClose}>Cancelar</NavButton>
      <NavButton primary onClick={onConfirm}>
        {confirmLabel}
      </NavButton>
    </DialogActions>
  </ModelDialog>
);

// ─── Tela de pedido ───────────────────────────────────────────────────────────

type ScreenProps = {
  flow: BasketResponseDTO;
  productId: number;
  product: DbProduct | null;
  /** "Voltar". Padrão: página anterior. */
  onExit?: () => void;
  /** Ao lado do título (ex.: o campo de busca do produto). */
  titleExtra?: ReactNode;
  /** Incorpora o questionário sem navegação ou gravação no pedido global. */
  embedded?: boolean;
  onConfigurationChange?: (value: {
    answers: import('./types').Answers;
    models: import('./types').ModelChoices;
    items: SelectedItem[];
    complete: boolean;
  }) => void;
};

/**
 * Questionário em duas abas (obrigatórias / complementares) à esquerda e a
 * descrição do equipamento à direita. Todos os cards ficam abertos; o botão
 * principal leva à primeira pendência ("Prosseguir") ou finaliza o pedido.
 */
export const BasketFlowScreen = ({
  flow,
  productId,
  product,
  onExit,
  titleExtra,
  embedded = false,
  onConfigurationChange,
}: ScreenProps) => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const q = useQuestionnaire(flow);
  const tabs = useMemo(() => tabOf(flow.questions), [flow]);

  const [tab, setTab] = useState<FlowTab>('principal');
  const [picker, setPicker] = useState<{ code: string; option: string } | null>(
    null
  );
  const [confirmReset, setConfirmReset] = useState(false);
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);

  const tabQuestions = q.visible.filter((item) => tabs.get(item.code) === tab);
  const answeredCount = tabQuestions.filter((item) =>
    isAnswered(item, q.answers, q.models)
  ).length;
  // Numeração dos blocos sobre todas as perguntas visíveis, igual nas duas abas.
  const blockNumbers = new Map<string, number>();
  groupBlocks(q.visible, flow.questions).forEach((group, index) =>
    group.forEach((item) => blockNumbers.set(item.code, index + 1))
  );
  const percent = Math.round(
    (answeredCount / Math.max(tabQuestions.length, 1)) * 100
  );
  const pending = firstMissing(q.visible, q.answers, tabs, q.models);
  useEffect(() => {
    onConfigurationChange?.({
      answers: q.answers,
      models: q.models,
      items: q.items,
      complete: !pending,
    });
  }, [q.answers, q.models, q.items, pending, onConfigurationChange]);

  const pickerQuestion = q.visible.find((item) => item.code === picker?.code);
  const pickerOption = pickerQuestion?.options.find(
    (item) => item.value === picker?.option
  );
  const pickerBasket = pickerOption ? modelChoiceOf(pickerOption) : undefined;

  // Rola até a pendência depois que a aba dela estiver na tela.
  useEffect(() => {
    if (!scrollTarget) return;
    q.scrollTo(scrollTarget);
    setScrollTarget(null);
  }, [scrollTarget, tab]);

  /** Entrega o pedido no formato da tela de finalizar da implantação. */
  const finish = () => {
    if (embedded) return;
    const { configuredEquipment, fullListProd } = toOrderData(
      q.visible,
      q.answers,
      q.models
    );
    const store = useOrderFlowStore.getState();
    store.setConfiguredEquipment(configuredEquipment);
    store.setFullListProd(fullListProd);
    navigate('/order/finish');
  };

  const proceed = () => {
    if (!pending) {
      finish();
      return;
    }
    setTab(tabs.get(pending.code) ?? 'principal');
    q.flag(pending.code);
    setScrollTarget(pending.code);
  };

  const reset = () => {
    q.reset();
    setTab('principal');
    setConfirmReset(false);
  };

  return (
    <PageShell
      sx={embedded ? { p: 0, height: 'auto', display: 'block' } : undefined}
    >
      {!embedded && (
        <TitleRow>
          <PageTitle>
            Gerando seu pedido{' '}
            <ContextLine productId={productId} product={product} />
          </PageTitle>
          {titleExtra}
        </TitleRow>
      )}

      <ContentGrid
        sx={
          embedded
            ? {
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  md: 'minmax(0, 40fr) minmax(0, 60fr)',
                },
                gridTemplateRows: 'auto',
                minHeight: 0,
                alignItems: 'start',
                gap: 2,
              }
            : undefined
        }
      >
        <QuestionColumn sx={embedded ? { order: 0 } : undefined}>
          <Card sx={embedded ? { boxShadow: 'none' } : undefined}>
            <CardTabsBar>
              <SectionTabs role="tablist" aria-label="Partes do pedido">
                {TABS.map((item) => (
                  <SectionTab
                    key={item.id}
                    type="button"
                    role="tab"
                    active={tab === item.id}
                    aria-selected={tab === item.id}
                    onClick={() => setTab(item.id)}
                  >
                    {item.label}
                  </SectionTab>
                ))}
              </SectionTabs>
              <TextAction
                startIcon={<RestartAlt />}
                disabled={Object.keys(q.answers).length === 0}
                onClick={() => setConfirmReset(true)}
              >
                Reiniciar
              </TextAction>
            </CardTabsBar>

            <StepHeader>
              <StepLabel>
                <strong>{percent}% concluído</strong>
              </StepLabel>
              <ProgressTrack>
                <ProgressFill percent={percent} />
              </ProgressTrack>
            </StepHeader>

            <StepBody>
              {tabQuestions.length === 0 && (
                <EmptyState>
                  {tab === 'principal'
                    ? 'Este produto não tem perguntas obrigatórias.'
                    : 'Este produto não tem terapias complementares.'}
                </EmptyState>
              )}
              <QuestionBlocks
                all={flow.questions}
                blockNumber={(question) => blockNumbers.get(question.code) ?? 0}
                questions={tabQuestions}
                answers={q.answers}
                models={q.models}
                errorFor={q.errorFor}
                blockRef={q.blockRef}
                onAnswer={q.answer}
                onPickModel={(question, option) =>
                  setPicker({ code: question.code, option })
                }
                onSelectProduct={(question, id) =>
                  q.setModel(question.code, id)
                }
              />
            </StepBody>

            <NavFooter>
              {!embedded && (
                <NavButton onClick={onExit ?? (() => navigate(-1))}>
                  Voltar
                </NavButton>
              )}
              {(!embedded || pending) && (
                <NavButton primary onClick={proceed}>
                  {pending ? 'Prosseguir' : 'Finalizar pedido'}
                </NavButton>
              )}
            </NavFooter>
          </Card>
        </QuestionColumn>

        <ProductColumn sx={embedded ? { order: 0 } : undefined}>
          <DescriptionPanel
            embedded={embedded}
            productId={productId}
            product={product}
            extras={q.items}
            blockNumber={(code) => blockNumbers.get(code)}
          />
        </ProductColumn>
      </ContentGrid>

      <ConfirmDialog
        open={confirmReset}
        title="Reiniciar as perguntas?"
        message="Todas as respostas e modelos escolhidos serão apagados."
        confirmLabel="Reiniciar"
        onConfirm={reset}
        onClose={() => setConfirmReset(false)}
      />
      <ModelPicker
        basket={pickerBasket}
        title={pickerQuestion?.text ?? ''}
        selectedId={
          pickerQuestion && q.answers[pickerQuestion.code] === picker?.option
            ? q.models[pickerQuestion.code]
            : undefined
        }
        isMobile={isMobile}
        onSelect={(productIdChoice) => {
          if (pickerQuestion && picker) {
            q.answer(pickerQuestion, picker.option);
            q.setModel(pickerQuestion.code, productIdChoice);
          }
          setPicker(null);
        }}
        onClose={() => setPicker(null)}
      />
    </PageShell>
  );
};

/** Cabeçalho + mensagem, para carregando / erro / sem fluxo. */
const StatusScreen = ({
  productId,
  product,
  titleExtra,
  children,
}: {
  productId: number;
  product: DbProduct | null;
  titleExtra: ReactNode;
  children: ReactNode;
}) => (
  <PageShell>
    <TitleRow>
      <PageTitle>
        Gerando seu pedido{' '}
        <ContextLine productId={productId} product={product} />
      </PageTitle>
      {titleExtra}
    </TitleRow>
    <Card>
      <EmptyState>{children}</EmptyState>
    </Card>
  </PageShell>
);

/**
 * Campo para testar o fluxo de outro produto do banco. No pedido real, o
 * produto vem da etapa de implantação; aqui o ID digitado vai para `?product=`.
 */
const ProductSearch = ({
  productId,
  onSearch,
}: {
  productId: number;
  onSearch: (id: number) => void;
}) => {
  const [text, setText] = useState(String(productId));
  useEffect(() => setText(String(productId)), [productId]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const id = Number(text.trim());
    if (Number.isInteger(id) && id > 0) onSearch(id);
  };

  return (
    <ProductSearchForm onSubmit={submit} role="search">
      <ProductIdInput
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="ID do produto"
        inputProps={{ inputMode: 'numeric', 'aria-label': 'ID do produto' }}
      />
      <NavButton type="submit" primary>
        Buscar
      </NavButton>
    </ProductSearchForm>
  );
};

/** Produto aberto sem `?product=<id>`: o que tem fluxo no banco. */
const DEFAULT_PRODUCT_ID = 6786;

/**
 * Tela de pedido montada com o JSON de GET /basket/:productId, sem
 * transformação. Os dados do equipamento vêm de GET /queryProducts.
 */
export const ConfigureEquipmentMockup = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = Number(searchParams.get('product'));
  const productId = requested > 0 ? requested : DEFAULT_PRODUCT_ID;

  const api = useApiQuestionnaire(productId);
  const { product } = useProduct(productId);

  const search = (
    <ProductSearch
      productId={productId}
      onSearch={(id) => setSearchParams({ product: String(id) })}
    />
  );

  const status = (content: ReactNode) => (
    <WideLayout
      outlet={
        <StatusScreen
          productId={productId}
          product={product}
          titleExtra={search}
        >
          {content}
        </StatusScreen>
      }
    />
  );

  if (api.status === 'loading' || api.status === 'idle') {
    return status(
      <>
        <Spinner />
        <br />
        Carregando o fluxo do banco…
      </>
    );
  }

  if (api.status === 'error') {
    return status(
      <>
        Não foi possível carregar o fluxo do banco.
        <br />
        <small>{api.error}</small>
        <br />
        <NavButton sx={{ mt: 2 }} onClick={api.retry}>
          Tentar novamente
        </NavButton>
      </>
    );
  }

  if (!api.data) {
    return status(
      'O banco não tem fluxo configurado para este produto (a API respondeu null).'
    );
  }

  return (
    <WideLayout
      outlet={
        <BasketFlowScreen
          key={productId}
          flow={api.data}
          productId={productId}
          product={product}
          titleExtra={search}
        />
      }
    />
  );
};

export default ConfigureEquipmentMockup;
